import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Plane } from "lucide-react";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/aboutus", label: "About" },
    { to: "/contactus", label: "Contact" },
  ];

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-md border-b border-slate-200"
            : "bg-white border-b border-slate-200"
        }`}
      >
        <nav className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto gap-4">
          {/* LOGO */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-700 rounded-lg flex items-center justify-center">
              <Plane size={16} className="text-white -rotate-45" />
            </div>
            <span
              className="text-base font-extrabold tracking-tight hidden sm:block"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: "#0f1e3d" }}
            >
              MyFly<span className="text-sky-500">Sky9</span>
            </span>
          </Link>

          {/* DESKTOP LINKS */}
          <div className="hidden md:flex items-center gap-1 text-sm font-semibold text-slate-600">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`px-4 py-2 rounded-lg transition-all duration-200 hover:bg-slate-100 hover:text-slate-900 ${
                  location.pathname === to ? "bg-sky-50 text-sky-600" : ""
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          {/* RIGHT — CTA + Hamburger */}
          <div className="flex items-center gap-2">
            {/* Desktop phone CTA */}
            <a
              href="tel:+18888888888"
              className="hidden md:flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-slate-900 px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 shadow-sm hover:shadow-md pulse-glow"
            >
              <Phone size={15} />
              +1 888-888-8888
            </a>

            {/* Mobile phone CTA */}
            <a
              href="tel:+18888888888"
              className="flex md:hidden items-center gap-1.5 bg-amber-400 text-slate-900 px-3 py-2 rounded-full text-xs font-bold pulse-glow"
            >
              <Phone size={13} />
              Call Now
            </a>

            {/* Hamburger */}
            <button
              onClick={() => setOpen((p) => !p)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </header>

      {/* MOBILE DRAWER */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="absolute right-0 top-0 h-full w-72 bg-white shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-700 rounded-lg flex items-center justify-center">
                  <Plane size={16} className="text-white -rotate-45" />
                </div>
                <span className="font-extrabold text-sm text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  MyFly<span className="text-sky-500">Sky9</span>
                </span>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>

            {/* Nav Links */}
            <nav className="flex flex-col px-4 py-4 gap-1">
              {navLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  className={`px-4 py-3 rounded-xl font-semibold text-sm transition ${
                    location.pathname === to
                      ? "bg-sky-50 text-sky-600"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {label}
                </Link>
              ))}
            </nav>

            {/* Call CTA */}
            <div className="mt-auto px-4 pb-6">
              <a
                href="tel:+18888888888"
                className="flex items-center justify-center gap-2 w-full bg-amber-400 text-slate-900 py-3.5 rounded-xl font-bold text-base pulse-glow"
              >
                <Phone size={17} />
                +1 888-888-8888
              </a>
              <p className="text-center text-xs text-slate-400 mt-2">24/7 Travel Helpline</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
